import FootballStatsCategory from "../../../../stats/football-stats-category";
import FootballState from "../../../../state/football-state";
import StatsRule from "../../../../../common/stats-rule";

/**
 * Stats rule for recording Bar Ball return stats (Indoor Football).
 *
 * Like a kickoff return, return yards are measured from where the ball was
 * kicked (the KickOff fragment's `kickedTo`) to where the returner was downed.
 * The down spot lives on the outcome fragment itself — `caughtAt` for the
 * reused ReboundNet catch states, `recoveredAt` for the Bar Ball recovered
 * states (each is captured by the wizard via `requiredAfterNextState:
 * [Tackle, OutOfBounds]`). The Tackle fragment carries no yardline.
 *
 * Bar Ball only occurs on a kickoff/FGA/PAT; only kickoff-originated plays have
 * a kick reference, so non-kickoff plays record a 0-yard return attempt.
 */
export default class BarBallReturnStats extends StatsRule {
    // eslint-disable-next-line no-unused-vars
    applicable(
        gameContext,
        ruleContext,
        eventConfiguration,
        { play, fragment }
    ) {
        const isReturnerOutcome = [
            FootballState.ReboundNetCaughtOffense,
            FootballState.ReboundNetCaughtDefense,
            FootballState.BarBallRecoveredKicking,
            FootballState.BarBallRecoveredReceiving
        ].includes(fragment.associatedState);

        return (
            isReturnerOutcome &&
            play.historyContains(FootballState.HitGoalpost) &&
            super.applicable(gameContext, ruleContext, eventConfiguration, {
                play,
                fragment
            })
        );
    }

    // eslint-disable-next-line no-unused-vars
    async apply(
        gameContext,
        ruleContext,
        eventConfiguration,
        { play, fragment }
    ) {
        // Catch outcomes carry caughtBy/caughtAt; recovered outcomes carry
        // recoveredBy/recoveredAt. The spot is where the returner was downed.
        const returnedBy = fragment.caughtBy || fragment.recoveredBy;
        const downSpot =
            fragment.caughtAt != null
                ? fragment.caughtAt
                : fragment.recoveredAt;

        if (!returnedBy || downSpot == null) {
            return [];
        }

        // Return is measured from where the ball was kicked. Bar Ball can also
        // arise on a FGA/PAT with no kickoff reference, in which case it is a
        // 0-yard return attempt.
        const kickOff = play.getFirstFragment(FootballState.KickOff);
        const kickedTo = kickOff?.kickedTo;

        const returnerIsHome = returnedBy.teamId === gameContext.homeTeamId;
        const directionMultiplier = returnerIsHome ? 1 : -1;
        const yardsReturned =
            kickedTo == null ? 0 : (downSpot - kickedTo) * directionMultiplier;

        const fieldLength = gameContext.getFieldLength();
        const resultedInTd = returnerIsHome
            ? downSpot >= fieldLength
            : downSpot <= 0;

        return this.addStats({
            gameId: play.gameId,
            playId: play.id,
            period: gameContext.currentPeriod,
            teamId: returnedBy.teamId,
            playerId: returnedBy.id,
            driveId: gameContext.currentDriveId,
            fragmentId: fragment.id,
            category: FootballStatsCategory.BAR_BALL_RETURN,
            values: {
                yards: yardsReturned,
                completed: 1,
                attempted: 1,
                touchdowns: resultedInTd ? 1 : 0
            }
        });
    }
}
